import { PERM, roleLabelFromPermission, isDepartmentManagerLogViewer } from 'pages/Settings/redux';
import {
  buildPermissionByActor,
  canViewActivityLog,
} from 'pages/Logs/activityLogsDisplay';
import {
  buildManagerLogAccessContext,
  type ManagerLogAccessRecord,
} from 'pages/Logs/managerLogAccess';
import { buildStaffMaps, resolveActorUser, type StaffMaps } from 'pages/Logs/logActor';
import type { Log } from 'pages/Logs/hooks';

export type StaffReportRecipient = {
  email: string,
  username: string,
  permission: number,
  staffId?: string,
};

/** Manager (5)+ may close a filed report only when sender and recipient are both below them. */
export const canCloseReviewedReportOfLowerStaff = (
  closerPermission: unknown,
  senderPermission: unknown,
  recipientPermission: unknown,
): boolean => {
  const closer = Number(closerPermission);
  const sender = Number(senderPermission);
  const recipient = Number(recipientPermission);
  if (!Number.isFinite(closer) || closer < PERM.MANAGER) return false;
  if (!Number.isFinite(sender) || !Number.isFinite(recipient)) return false;
  return sender < closer && recipient < closer;
};

/**
 * Standing-log message: "… by |sender|, filed by |reviewer|".
 * Filer is the recipient who filed. Hide Close if this viewer could not close.
 */
export const canCloseFiledReportStandingLog = (
  message: string,
  closerPermission: unknown,
  staffMaps: StaffMaps,
): boolean => {
  const text = String(message || '');
  const filerToken = text.match(/,\s*filed by \|([^|]+)\|\s*$/i)?.[1];
  const senderToken = text.match(/\sby \|([^|]+)\|,\s*filed by /i)?.[1];
  if (!filerToken || !senderToken) return false;
  const senderPerm = Number(resolveActorUser(senderToken, staffMaps)?.permission);
  const recipientPerm = Number(resolveActorUser(filerToken, staffMaps)?.permission);
  return canCloseReviewedReportOfLowerStaff(closerPermission, senderPerm, recipientPerm);
};

export const canReceiveNewStaffReport = (permission: unknown): boolean => {
  const p = Number(permission);
  return Number.isFinite(p) && p >= PERM.SUB_MANAGER;
};

/**
 * Reply reports may go to the original sender, or to anyone at or below
 * the sender's permission (down to Employee). Never above the sender.
 */
export const canReceiveReplyStaffReport = (
  senderPermission: unknown,
  recipientPermission: unknown,
): boolean => {
  const sender = Number(senderPermission);
  const recipient = Number(recipientPermission);
  if (!Number.isFinite(sender) || !Number.isFinite(recipient)) return false;
  if (recipient < PERM.EMPLOYEE) return false;
  return recipient <= sender;
};

export const staffReportRoleLabel = (permission: number): string => (
  roleLabelFromPermission(permission)
);

export type StaffReportLogViewAccess = {
  permission: number,
  staffId?: string | null,
  username?: string | null,
  /** Recipient is below the sender — logs must be capped to their view. */
  recipientHasLowerPermission: boolean,
};

/**
 * Logs in a report never use a higher permission than either person.
 * Same permission → sender identity. Lower recipient → their identity + perm.
 */
export const staffReportLogViewAccess = (
  senderPermission: number,
  senderStaffId: string | null | undefined,
  senderUsername: string | null | undefined,
  recipient: StaffReportRecipient | null | undefined,
): StaffReportLogViewAccess => {
  const senderPerm = Number(senderPermission) || 0;
  if (!recipient) {
    return {
      permission: senderPerm,
      staffId: senderStaffId,
      username: senderUsername,
      recipientHasLowerPermission: false,
    };
  }
  const recipientPerm = Number(recipient.permission) || 0;
  const viewPerm = Math.min(senderPerm, recipientPerm);
  const recipientHasLowerPermission = recipientPerm < senderPerm;
  if (recipientHasLowerPermission) {
    return {
      permission: viewPerm,
      staffId: recipient.staffId || null,
      username: recipient.username || null,
      recipientHasLowerPermission: true,
    };
  }
  return {
    permission: viewPerm,
    staffId: senderStaffId,
    username: senderUsername,
    recipientHasLowerPermission: false,
  };
};

/**
 * Associate Manager (4) and below do not see every activity.
 * An activity set in the report grants those logs — tell the sender.
 */
export const staffReportRecipientGainsHiddenLogsNote = (
  viewAccess: StaffReportLogViewAccess,
): boolean => (
  viewAccess.recipientHasLowerPermission && viewAccess.permission < PERM.MANAGER
);

/** Source ids the chosen recipient is allowed to see (never above sender perm). */
export const visibleActivitySourceIdsForRecipient = (
  logs: Log[] | null | undefined,
  users: {
    id?: string | number | null
    username?: string | null
    permission?: string | number | null
    email?: string | null
  }[],
  viewAccess: StaffReportLogViewAccess,
  erpRecords?: ManagerLogAccessRecord[] | null,
): Set<string> | null => {
  // No cap needed, or logs not loaded yet — keep the sender's current set.
  if (!viewAccess.recipientHasLowerPermission) return null;
  if (!logs) return null;
  const staffMaps = buildStaffMaps(users);
  const permissionByActor = buildPermissionByActor(users);
  const managerContext = isDepartmentManagerLogViewer(viewAccess.permission)
    ? buildManagerLogAccessContext(erpRecords || [], users, viewAccess.staffId, viewAccess.username)
    : null;
  const visible = new Set<string>();
  logs.forEach((log) => {
    if (!canViewActivityLog(log, {
      permission: viewAccess.permission,
      staffId: viewAccess.staffId,
      username: viewAccess.username,
      staffMaps,
      permissionByActor,
      // Sender elevate must not leak restricted logs to a lower-perm recipient.
      complianceReviewActive: false,
      managerContext,
    })) return;
    if (log.id != null && String(log.id) !== '') visible.add(String(log.id));
  });
  return visible;
};

export const activitySourceIdIsVisible = (
  sourceId: string | undefined,
  visibleIds: Set<string> | null,
): boolean => {
  if (!visibleIds) return true;
  const id = String(sourceId || '').trim();
  if (!id) return false;
  return visibleIds.has(id);
};

export type ReportItemShowLogsViewAs = {
  permission: number,
  staffId?: string,
  username?: string,
  label?: string,
};

type ReportItemAuthorLookup = {
  email?: string | null,
  fromEmail?: string | null,
  fromName?: string | null,
  isOutgoing?: boolean,
};

type ReportItemAuthorStaff = {
  email?: string | null,
  id?: string | number | null,
  username?: string | null,
  permission?: string | number | null,
};

/**
 * Lower-access staff cannot open Show all logs on a higher-access author's report.
 * Own items and same/lower authors still get the jump.
 */
export const reportAuthorHasHigherAccessThanViewer = (
  item: ReportItemAuthorLookup,
  myEmail: string,
  myPermission: unknown,
  users: ReportItemAuthorStaff[] | null | undefined,
): boolean => {
  const me = String(myEmail || '').trim().toLowerCase();
  const from = String(item.fromEmail || item.email || '').trim().toLowerCase();
  if (item.isOutgoing || (me && from && from === me)) return false;
  const author = (users || []).find((user) => (
    String(user?.email || '').trim().toLowerCase() === from
  ));
  if (!author) return false;
  const authorPerm = Number(author.permission);
  const mine = Number(myPermission);
  if (!Number.isFinite(authorPerm) || !Number.isFinite(mine)) return false;
  return authorPerm > mine;
};

/**
 * Show logs uses the author of the opened item.
 * Your sent report → no view-as (you). Their report or reply → them.
 * ActivityLogs still never raises the signed-in permission.
 */
export const reportItemShowLogsViewAs = (
  item: ReportItemAuthorLookup,
  myEmail: string,
  users: ReportItemAuthorStaff[] | null | undefined,
  myStaffId?: string | null,
  myUsername?: string | null,
): ReportItemShowLogsViewAs | null => {
  const me = String(myEmail || '').trim().toLowerCase();
  const from = String(item.fromEmail || item.email || '').trim().toLowerCase();
  const selfId = myStaffId != null && String(myStaffId) !== ''
    ? String(myStaffId)
    : '';
  const selfName = String(myUsername || '').trim().toLowerCase();
  // Own report / own reply — stay as the signed-in staff.
  if (item.isOutgoing || (me && from && from === me)) return null;
  const author = (users || []).find((user) => (
    String(user?.email || '').trim().toLowerCase() === from
  ));
  if (!author) return null;
  if (selfId && author.id != null && String(author.id) === selfId) return null;
  if (selfName && String(author.username || '').trim().toLowerCase() === selfName) {
    return null;
  }
  const permission = Number(author.permission);
  if (!Number.isFinite(permission)) return null;
  const username = String(author.username || item.fromName || '').trim() || undefined;
  return {
    permission: Math.floor(permission),
    staffId: author.id != null && String(author.id) !== ''
      ? String(author.id)
      : undefined,
    username,
    label: username,
  };
};
